/**
 * The payroll engine rollout contract (ADR 0012, phase 4a): which calculation path a legal entity
 * runs, the explicit entity-level default facts an entity may define, the readiness report, and the
 * shadow parity results.
 *
 * LEGACY IS AUTHORITATIVE in `legacy` and `shadow`: `shadow` runs the new engine beside the legacy one and stores the
 * difference; it never changes what anyone is paid. In `engine` (phase 4b) the ENGINE is authoritative and legacy runs
 * beside it as a comparator. Entering `engine` needs `payroll.rules:rollout` AND a readiness report with no blocker.
 *
 * ABSENT IS NOT FALSE, AND NOTHING IS INFERRED. An entity default is an explicit, effective-dated,
 * reasoned record, and every fact it supplies says so in its provenance (`source: 'entity_default'`).
 *
 * Money in this contract is a decimal STRING (CLAUDE.md invariant 2). RN-safe: no Node built-ins.
 */

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import {
  dateOnlySchema,
  errorBodySchema,
  idempotencyKeySchema,
  instantSchema,
  uuidSchema,
} from '../schemas/common.js';
import {
  payrollFactProvenanceSchema,
  payrollRuleActorSchema,
  payrollRuleCodeSchema,
  payrollSha256Schema,
} from './payroll-rules.contract.js';
import { routeMeta } from './meta.js';

const c = initContract();

const idempotencyHeadersSchema = z.object({ 'idempotency-key': idempotencyKeySchema });
const authErrors = { 401: errorBodySchema, 403: errorBodySchema } as const;

// ---------------------------------------------------------------------------
// The rollout flag
// ---------------------------------------------------------------------------

export const PAYROLL_ENGINE_MODES = ['legacy', 'shadow', 'engine'] as const;
export const payrollEngineModeSchema = z.enum(PAYROLL_ENGINE_MODES);
export type PayrollEngineMode = z.infer<typeof payrollEngineModeSchema>;

/**
 * Whether an already-resolved mode value (e.g. `payrollRollout.mode`, `payrollRollout.getReadiness`'s
 * `mode`) is the engine. The one named place a CLIENT should ask this — `packages/payroll-rules/src/
 * retirement-rollout.ts`'s `isEngineMode` is the server-side equivalent over a raw, possibly-absent
 * rollout ROW (`mode ?? 'legacy'` when there is none), which is a genuinely different question and
 * lives in a server-only package `apps/web`/`apps/mobile` cannot import (it depends on `@hrms/db`).
 * A client never sees a missing row — every response that carries `mode` has already resolved it
 * server-side — so this is exactly the same rule with no null case to consider, named here instead of
 * repeated as an inline `.mode === 'engine'` at every call site (packages/payroll-rules's own
 * retirement-inventory ratchet, `inlineRolloutRule`, flags exactly that pattern wherever it appears,
 * including in client code that reads the resolved field).
 */
export const isEngineMode = (mode: PayrollEngineMode): boolean => mode === 'engine';

/**
 * What a request may ASK for. `engine` is selectable only by `payroll.rules:rollout`, and only for an entity whose
 * readiness report has no blocker (ADR 0012, phase 4b); leaving it needs only `payroll.rules:manage`.
 */
export const PAYROLL_ENGINE_MODES_AVAILABLE = ['legacy', 'shadow', 'engine'] as const;

export const payrollRolloutSchema = z.object({
  legalEntityId: uuidSchema,
  mode: payrollEngineModeSchema,
  availableModes: z.array(payrollEngineModeSchema),
  changedAt: instantSchema.nullable(),
  changedBy: payrollRuleActorSchema.nullable(),
});
export type PayrollRollout = z.infer<typeof payrollRolloutSchema>;

export const payrollRolloutParamsSchema = z.object({ legalEntityId: uuidSchema });

export const setPayrollRolloutModeBodySchema = z.object({ mode: payrollEngineModeSchema });

// ---------------------------------------------------------------------------
// Entity-level default facts
// ---------------------------------------------------------------------------

export const PAYROLL_DEFAULT_FACT_KINDS = ['flag', 'input', 'attribute'] as const;
export const payrollDefaultFactKindSchema = z.enum(PAYROLL_DEFAULT_FACT_KINDS);

export const payrollEntityFactDefaultSchema = z.object({
  id: uuidSchema,
  legalEntityId: uuidSchema,
  factName: z.string().regex(/^[a-z][a-zA-Z0-9_]*$/),
  kind: payrollDefaultFactKindSchema,
  /** `true` / `false` for a flag, a decimal string for an input, a string for an attribute. */
  value: z.string(),
  validFrom: dateOnlySchema,
  validTo: dateOnlySchema.nullable(),
  reason: z.string(),
  createdBy: payrollRuleActorSchema,
  createdAt: instantSchema,
});
export type PayrollEntityFactDefault = z.infer<typeof payrollEntityFactDefaultSchema>;

export const createPayrollEntityFactDefaultBodySchema = z
  .object({
    legalEntityId: uuidSchema,
    factName: z.string().regex(/^[a-z][a-zA-Z0-9_]*$/),
    kind: payrollDefaultFactKindSchema,
    value: z.string().trim().min(1).max(60),
    validFrom: dateOnlySchema,
    reason: z.string().trim().min(3).max(500),
  })
  .strict();

export const endPayrollEntityFactDefaultBodySchema = z.object({ validTo: dateOnlySchema });
export const payrollEntityFactDefaultParamsSchema = z.object({ defaultId: uuidSchema });
export const listPayrollEntityFactDefaultsQuerySchema = z.object({
  legalEntityId: uuidSchema,
  asOf: dateOnlySchema.optional(),
});

// ---------------------------------------------------------------------------
// Parity
// ---------------------------------------------------------------------------

/** Only `unexplained` and `calculation_error` fail the parity gate. */
export const PAYROLL_PARITY_CLASSES = [
  'identical',
  'accepted_divergence',
  'unexplained',
  'unsupported',
  'missing_input',
  'calculation_error',
] as const;
export const payrollParityClassSchema = z.enum(PAYROLL_PARITY_CLASSES);
export type PayrollParityClass = z.infer<typeof payrollParityClassSchema>;
export const PAYROLL_PARITY_GATE_FAILING_CLASSES = ['unexplained', 'calculation_error'] as const;

export const PAYROLL_SHADOW_STATUSES = [
  'completed',
  /** No effective rules resolved for the period (or the pack is not seeded). */
  'rules_unavailable',
  /** The shadow itself failed. The legacy calculation was unaffected. */
  'failed',
  /** The run was finalized, or the version was superseded, before the shadow could run. */
  'skipped',
] as const;
export const payrollShadowStatusSchema = z.enum(PAYROLL_SHADOW_STATUSES);

export const payrollParityFindingSchema = z.object({
  /** A result field (`fixedGross`, `netPayable`) or a legacy component code (`PF_EMPLOYEE`). */
  field: z.string(),
  legacy: z.string().nullable(),
  engine: z.string().nullable(),
  class: payrollParityClassSchema,
  /** An accepted-divergence code, an unsupported reason, or a missing fact name. */
  code: z.string().nullable(),
  detail: z.string().nullable(),
});
export type PayrollParityFinding = z.infer<typeof payrollParityFindingSchema>;

/** An engine output no legacy component receives: recorded, never dropped, never given a new code. */
export const payrollUnmappedOutputSchema = z.object({
  ruleCode: payrollRuleCodeSchema,
  amount: z.string(),
  reason: z.string(),
});

export const payrollParityEmployeeSchema = z.object({
  employmentId: uuidSchema,
  class: payrollParityClassSchema,
  findings: z.array(payrollParityFindingSchema),
  unmapped: z.array(payrollUnmappedOutputSchema),
  missingFacts: z.array(z.string()),
  facts: z.array(payrollFactProvenanceSchema),
});
export type PayrollParityEmployee = z.infer<typeof payrollParityEmployeeSchema>;

export const payrollParityReportSchema = z.object({
  runId: uuidSchema,
  calculationVersionId: uuidSchema,
  legalEntityId: uuidSchema,
  status: payrollShadowStatusSchema,
  /** A code, never prose that could carry a figure. */
  reasonCode: z.string().nullable(),
  engineVersion: z.string(),
  rulesChecksum: payrollSha256Schema.nullable(),
  rulesAsOf: dateOnlySchema.nullable(),
  counts: z.record(z.number().int().nonnegative()),
  /** True when nothing is `unexplained` or `calculation_error`. Null until a comparison completed. */
  gatePassed: z.boolean().nullable(),
  completedAt: instantSchema.nullable(),
  employees: z.array(payrollParityEmployeeSchema),
});
export type PayrollParityReport = z.infer<typeof payrollParityReportSchema>;

export const payrollParityParamsSchema = z.object({ runId: uuidSchema });
export const payrollParityQuerySchema = z.object({ calculationVersionId: uuidSchema.optional() });

// ---------------------------------------------------------------------------
// The professional-tax equivalence check (ADR 0012, phase 4b) — READ ONLY
// ---------------------------------------------------------------------------

/**
 * Does the platform pack's PT table reproduce what the entity's STORED professional-tax rule makes legacy
 * pay? It classifies nothing: it is not a parity class, not an accepted divergence, and feeds no gate.
 *
 * TWO DIFFERENCES ARE KEPT APART. `fullMonth` compares the TABLES over every wage (exact, not sampled).
 * `partMonth` is the WAGE-BASIS difference — legacy reads the full-month gross, the pack the pro-rated one —
 * which exists even when the tables are identical, and is never folded into the verdict.
 */
export const PAYROLL_PT_VERDICTS = ['equivalent', 'not_equivalent', 'not_assessable'] as const;

export const payrollPtSlabSchema = z.object({
  fromAmount: z.string(),
  toAmount: z.string().nullable(),
  monthlyTax: z.string(),
});
export const payrollPtBandSchema = z.object({
  from: z.string(),
  /** Exclusive; null for the top band. */
  upTo: z.string().nullable(),
  value: z.string(),
});
export const payrollPtSeveritySchema = z.enum(['blocking', 'warning']);
export const payrollPtReasonSchema = z.object({
  code: z.string(),
  severity: payrollPtSeveritySchema,
  detail: z.string(),
});
export const payrollPtDifferenceSchema = z.object({
  from: z.string(),
  upTo: z.string().nullable(),
  stored: z.string().nullable(),
  pack: z.string().nullable(),
});
export const payrollPtBoundaryIssueSchema = z.object({
  code: z.string(),
  severity: payrollPtSeveritySchema,
  slabIndex: z.number().int().nonnegative(),
  detail: z.string(),
});
export const payrollPtPartMonthSchema = z.object({
  legacyBasis: z.string(),
  packBasis: z.string().nullable(),
  /** True when the table pays different amounts at different wages. */
  exposed: z.boolean(),
  boundaries: z.array(
    z.object({
      at: z.string(),
      amountBelow: z.string(),
      amountAbove: z.string(),
      zones: z.array(
        z.object({
          daysPaid: z.number().int(),
          daysInPeriod: z.number().int(),
          /** A full-month gross at or above the boundary and below this pays the lower amount once pro-rated. */
          fullMonthGrossBelow: z.string(),
        }),
      ),
    }),
  ),
});

/**
 * The tables that apply to a gender and/or month (ADR 0019), compared class by class. Only classes where the two sides pay
 * differently are listed; `gender: null` is "any gender no variant names".
 */
export const payrollPtVariantScenarioSchema = z.object({
  gender: z.string().nullable(),
  months: z.array(z.number().int().min(1).max(12)),
  differences: z.array(payrollPtDifferenceSchema),
});

export const payrollPtEquivalenceSchema = z.object({
  legalEntityId: uuidSchema,
  asOf: dateOnlySchema,
  /** Always true: the check mutates nothing. */
  readOnly: z.literal(true),
  verdict: z.enum(PAYROLL_PT_VERDICTS),
  /** Blocking reasons first, then warnings. A code and a sentence about tax slabs — never a salary. */
  reasons: z.array(payrollPtReasonSchema),
  stored: z
    .object({
      ruleVersionId: uuidSchema,
      validFrom: dateOnlySchema,
      stateCode: z.string(),
      slabs: z.array(payrollPtSlabSchema),
      /** The bands legacy's rule actually implies (its `fromAmount`s alone decide). */
      bands: z.array(payrollPtBandSchema),
      boundaryIssues: z.array(payrollPtBoundaryIssueSchema),
    })
    .nullable(),
  pack: z
    .object({
      ruleSetCode: payrollRuleCodeSchema,
      versionNo: z.number().int().nullable(),
      definitionChecksum: payrollSha256Schema.nullable(),
      /** The state the platform table is for; null for the state-less workbook table. */
      jurisdiction: z.string().nullable(),
      bands: z.array(payrollPtBandSchema),
      /** How the pack's rule reads the wage; null when it is not the shape this check recognises. */
      wageBasis: z.string().nullable(),
    })
    .nullable(),
  state: z.object({
    stored: z.string().nullable(),
    /** `payroll_entity_settings.pt_state_code`: what actually selects the platform table. */
    entitySetting: z.string().nullable(),
    entityAddress: z.string().nullable(),
    packJurisdiction: z.string().nullable(),
    resolvedJurisdictions: z.array(z.string()),
  }),
  fullMonth: z
    .object({
      equivalent: z.boolean(),
      /** Exact gross ranges `[from, upTo)` where the two tables pay different amounts. */
      differences: z.array(payrollPtDifferenceSchema),
    })
    .nullable(),
  /** Null when neither the stored rule nor the platform table has any variant: the account does not exist then. */
  variants: z
    .object({ scenarios: z.array(payrollPtVariantScenarioSchema) })
    .nullable()
    .optional(),
  partMonth: payrollPtPartMonthSchema.nullable(),
});
export type PayrollPtEquivalence = z.infer<typeof payrollPtEquivalenceSchema>;

export const payrollPtEquivalenceQuerySchema = z.object({ asOf: dateOnlySchema.optional() });

/**
 * THE PT RESULT AS READINESS REPORTS IT (ADR 0012, phase 4b): the equivalence check's verdict and evidence,
 * arranged so the four things it keeps apart stay apart, and an admin can see exactly why an entity is
 * blocked without opening the separate endpoint.
 *
 *   table       do the stored slabs pay what the platform table pays, at every wage?
 *   boundaries  are the stored slabs' own boundaries valid (no gap, overlap, duplicate, unbounded top)?
 *   state       does the stored state agree with the entity's PT setting and the platform table's state?
 *   partMonth   the wage-basis difference in a part month. NEVER folded into `verdict`.
 *
 * `covers` and `notCovered` say what the verdict does and does not vouch for, so a full-month equivalent
 * table is never read as universally equivalent. None of this is a parity class or an accepted divergence.
 */
export const PAYROLL_PT_COVERAGE = [
  'full_month_table',
  'slab_boundaries',
  'state_resolution',
  /** Present only when a side has variants (ADR 0019): every gender/month class was compared. */
  'variant_tables',
] as const;

export const payrollReadinessPtSchema = z.object({
  verdict: z.enum(PAYROLL_PT_VERDICTS),
  /** What `verdict` vouches for. Empty when equivalence could not be assessed. */
  covers: z.array(z.enum(PAYROLL_PT_COVERAGE)),
  /** What it does NOT vouch for: `part_month_wage_basis` unless the table pays one amount everywhere. */
  notCovered: z.array(z.enum(['part_month_wage_basis'])),
  table: z.object({
    equivalent: z.boolean().nullable(),
    /** Exact gross ranges `[from, upTo)` where the two tables pay different amounts. */
    differences: z.array(payrollPtDifferenceSchema),
  }),
  boundaries: z.object({
    /** No blocking boundary problem in the stored slabs. Null when there is no readable stored rule. */
    valid: z.boolean().nullable(),
    issues: z.array(payrollPtBoundaryIssueSchema),
  }),
  state: z.object({
    /** No blocking state problem. Null when there is nothing to compare. */
    consistent: z.boolean().nullable(),
    stored: z.string().nullable(),
    entitySetting: z.string().nullable(),
    entityAddress: z.string().nullable(),
    packJurisdiction: z.string().nullable(),
    resolvedJurisdictions: z.array(z.string()),
    issues: z.array(payrollPtReasonSchema),
  }),
  /** The wage-basis difference on its own; null when it cannot be stated. */
  partMonth: payrollPtPartMonthSchema.nullable(),
  /** Every reason, blocking first. */
  reasons: z.array(payrollPtReasonSchema),
  storedRule: z
    .object({ ruleVersionId: uuidSchema, validFrom: dateOnlySchema, stateCode: z.string() })
    .nullable(),
  packTable: z
    .object({
      ruleSetCode: payrollRuleCodeSchema,
      versionNo: z.number().int().nullable(),
      jurisdiction: z.string().nullable(),
      definitionChecksum: payrollSha256Schema.nullable(),
    })
    .nullable(),
});
export type PayrollReadinessPt = z.infer<typeof payrollReadinessPtSchema>;

// ---------------------------------------------------------------------------
// Readiness
// ---------------------------------------------------------------------------

export const payrollReadinessAttributeSchema = z.object({
  /** `vpf`, `esiApplicable`, `ptApplicable`, `skillLevel`. */
  name: z.string(),
  employees: z.number().int().nonnegative(),
  recorded: z.number().int().nonnegative(),
  coveredByEntityDefault: z.number().int().nonnegative(),
  unresolved: z.number().int().nonnegative(),
  /** Up to 50 ids, so an admin can go and fix them. */
  unresolvedEmploymentIds: z.array(uuidSchema),
});

export const payrollReadinessMigrationSchema = z.object({
  /** The stored statutory rule version this came from. */
  storedRuleVersionId: uuidSchema,
  storedRuleType: z.string(),
  storedKey: z.string(),
  storedValue: z.string().nullable(),
  /** `EPF_ER.rate`, or null when nothing configurable can hold it. */
  parameter: z.string().nullable(),
  packDefault: z.string().nullable(),
  /**
   * `statutory_fixed` (ADR 0012, "Company-editable options"): the stored value's parameter is classified `statutory`, so no
   * company tier can hold it and it is never layered into the engine's inputs. `differsFromPackDefault` then says whether the
   * stored value contradicts the published rule; only a differing one is a blocker (`stored_statutory_value_deviates`).
   */
  status: z.enum(['preserved', 'unmapped', 'unsupported', 'statutory_fixed']),
  differsFromPackDefault: z.boolean(),
  /**
   * True when the stored value is LAYERED into the engine's inputs (no published tenant configuration sets that parameter, so
   * nothing takes precedence over it). A layered value that differs from the platform default is an unverified engine input
   * and is a readiness blocker (`stored_statutory_value_unverified`, ADR 0012 decision 3, amended). Absent on a report
   * produced before this field existed.
   */
  layered: z.boolean().optional(),
  note: z.string().nullable(),
  /**
   * Set on `pt.slabs` and `pt.stateCode`: they are a table, not a parameter, so `unmapped` says only that
   * no parameter holds them. The PT equivalence check assesses them; see `professionalTax`.
   */
  assessment: z
    .object({ by: z.literal('pt_equivalence'), verdict: z.enum(PAYROLL_PT_VERDICTS) })
    .optional(),
});

export const payrollReadinessMappingSchema = z.object({
  engineRule: payrollRuleCodeSchema,
  legacyTarget: z.string().nullable(),
  status: z.enum(['mapped', 'mapped_if_present', 'unmapped']),
});

/**
 * The evidence behind a structural blocker: how many employees, and up to ten examples with the actual
 * figures. Derived from the stored comparison — never a classification, never an accepted divergence.
 */
export const payrollBlockerEvidenceSchema = z.object({
  employees: z.number().int().nonnegative(),
  examples: z.array(
    z.object({
      employmentId: uuidSchema,
      /** Named decimal strings, e.g. `monthlyCtc`, `templateGross`, `packGross`. */
      values: z.record(z.string()),
    }),
  ),
  /** Which engine rules, for a blocker that is about outputs (`employer_costs_unmapped`). */
  rules: z
    .array(z.object({ ruleCode: payrollRuleCodeSchema, employees: z.number().int() }))
    .optional(),
});
export type PayrollBlockerEvidence = z.infer<typeof payrollBlockerEvidenceSchema>;

/**
 * A CAPABILITY BLOCKER (ADR 0012, phase 4b): something the engine cannot actually reproduce, so that entering
 * engine mode would silently give an incorrect or incomplete payroll. It is derived from what the engine
 * itself can and cannot do — never from what legacy supports — and it is NOT a parity class: it changes no
 * classification, no gate, and creates no accepted divergence.
 *
 * `missing` names the capability; `unsafeBecause` says what would go wrong if an entity entered engine mode
 * regardless; `legacyBehaviour` says what legacy does today, for the reader, and never clears the blocker.
 */
export const PAYROLL_CAPABILITIES = [
  'income_tax',
  'template_components',
  'inputs',
  'output_parity',
  /** A product, compliance or Finance decision has not been made, so the engine must not assume one (ADR 0012). */
  'pending_decision',
] as const;

export const payrollCapabilityEvidenceSchema = z.object({
  capability: z.enum(PAYROLL_CAPABILITIES),
  /** Exactly what the engine cannot do. */
  missing: z.string(),
  /** Why entering engine mode would be unsafe. */
  unsafeBecause: z.string(),
  /** What legacy does today. Informational: legacy supporting it never clears the blocker. */
  legacyBehaviour: z.string().nullable(),
  /** Distinct employees the gap touches. */
  affectedEmployees: z.number().int().nonnegative(),
  items: z.array(
    z.object({ code: z.string(), detail: z.string(), employees: z.number().int().nonnegative() }),
  ),
});
export type PayrollCapabilityEvidence = z.infer<typeof payrollCapabilityEvidenceSchema>;

/**
 * A RULE WINDOW ENDING (ADR 0012, phase 4b): a platform rule-set version this entity's rules resolve to ends soon and no
 * successor covers the next day. From the day after `validTo` an engine-mode lock is REFUSED (the rules do not resolve).
 *
 * The successor's figures are statutory data: they are supplied and verified by `owner`, then published through the
 * platform's own lifecycle (validated structurally, author and publisher different, every write audited). Engineering
 * does not author, extend or replace them.
 */
export const payrollRulesWindowEvidenceSchema = z.object({
  /**
   * Versions ending within this many days of the report date are listed. A CONFIGURABLE warning horizon
   * (`PAYROLL_RULES_SUCCESSOR_HORIZON_DAYS`), not a statutory or compliance period.
   */
  horizonDays: z.number().int().positive(),
  /** The ROLE that must supply the compliance-verified successor (never a named person). */
  owner: z.string(),
  items: z.array(
    z.object({
      ruleSetCode: payrollRuleCodeSchema,
      versionNo: z.number().int(),
      validTo: dateOnlySchema,
      daysRemaining: z.number().int().nonnegative(),
      /** The first period end an engine lock is refused for: the day after `validTo`. */
      firstRefusedPeriodEnd: dateOnlySchema,
      /** `missing`: nothing published after it. `gap`: a later version exists but does not start the next day. */
      successor: z.enum(['missing', 'gap']),
      successorStartsOn: dateOnlySchema.nullable(),
    }),
  ),
});
export type PayrollRulesWindowEvidence = z.infer<typeof payrollRulesWindowEvidenceSchema>;

export const payrollReadinessBlockerSchema = z.object({
  code: z.string(),
  message: z.string(),
  evidence: payrollBlockerEvidenceSchema.optional(),
  /** On the PT configuration blockers: the whole PT account, so the blocker explains itself. */
  professionalTax: payrollReadinessPtSchema.optional(),
  /** On the capability blockers: what the engine cannot reproduce and why entry is unsafe. */
  capability: payrollCapabilityEvidenceSchema.optional(),
  /** On `rules_successor_missing`: which rule windows end, when, and who must supply the successor. */
  rulesWindow: payrollRulesWindowEvidenceSchema.optional(),
});

export const payrollReadinessSchema = z.object({
  legalEntityId: uuidSchema,
  asOf: dateOnlySchema,
  mode: payrollEngineModeSchema,
  /** Never true while any blocker exists. In 4a nothing can be switched to `engine`. */
  readyForEngine: z.boolean(),
  blockers: z.array(payrollReadinessBlockerSchema),
  rules: z.object({
    resolved: z.boolean(),
    reasonCode: z.string().nullable(),
    versions: z.array(
      z.object({
        ruleSetCode: payrollRuleCodeSchema,
        versionNo: z.number().int(),
        /** The window the version is in force for; `validTo` null is open-ended. */
        validFrom: dateOnlySchema.nullable().optional(),
        validTo: dateOnlySchema.nullable().optional(),
      }),
    ),
    rulesChecksum: payrollSha256Schema.nullable(),
  }),
  attributes: z.array(payrollReadinessAttributeSchema),
  entityDefaults: z.array(payrollEntityFactDefaultSchema),
  storedConfiguration: z.array(payrollReadinessMigrationSchema),
  /** The PT equivalence check's result, arranged as four separate accounts. Always present. */
  professionalTax: payrollReadinessPtSchema,
  /** Every stored value that differs from the pack default — the subset an admin must review. */
  packDefaultDifferences: z.array(payrollReadinessMigrationSchema),
  componentMappings: z.array(payrollReadinessMappingSchema),
  /** From the latest completed comparison: engine outputs no legacy component receives. */
  unmappedOutputs: z.array(
    z.object({ ruleCode: payrollRuleCodeSchema, employees: z.number().int() }),
  ),
  /** Facts the composition needs that nobody supplies, by name, with how many employees lack them. */
  missingFacts: z.array(z.object({ name: z.string(), employees: z.number().int() })),
  latestParity: z
    .object({
      calculationVersionId: uuidSchema,
      status: payrollShadowStatusSchema,
      counts: z.record(z.number().int().nonnegative()),
      gatePassed: z.boolean().nullable(),
    })
    .nullable(),
});
export type PayrollReadiness = z.infer<typeof payrollReadinessSchema>;

export const payrollReadinessQuerySchema = z.object({ asOf: dateOnlySchema.optional() });

// ---------------------------------------------------------------------------
// The CTC migration preview (ADR 0012, phase 4b) — READ ONLY
// ---------------------------------------------------------------------------

/**
 * WHAT THIS IS. For each CTC template an entity uses: what the template pays today (the legacy
 * calculation of a full month, exactly as payroll would run it) beside what the engine would pay for the
 * same CTC with Cost to Company semantics (employer contributions inside the CTC). It WRITES NOTHING: it
 * converts no template, changes no compensation, and creates no comparison, run or configuration.
 *
 * HOW TO READ THE FIGURES.
 *  - "Full month": the calendar month of `asOf`, no loss of pay. The preview has no attendance.
 *  - "Before income tax": both sides exclude income tax, because the pack has no tax section (TDS stays
 *    `unsupported`). The net is therefore not a payslip's net; the DIFFERENCE between the two sides is.
 *  - "Proposed" is the pack's structure at that CTC (Basic floor, HRA, allowance). It does not preserve a
 *    template's own percentages: `percentOfCtc` is what a migrated template would have to express.
 *  - Money is a decimal string.
 */
export const PAYROLL_CTC_PREVIEW_BASES = ['gross_equals_ctc', 'gross_below_ctc'] as const;

export const payrollCtcPreviewAmountLineSchema = z.object({
  code: z.string(),
  name: z.string(),
  amount: z.string(),
});

export const payrollCtcPreviewCurrentSchema = z.object({
  /** Whether the template's earnings ARE the whole CTC (the template treats CTC as gross pay). */
  ctcBasis: z.enum(PAYROLL_CTC_PREVIEW_BASES),
  basic: z.string().nullable(),
  fixedGross: z.string(),
  earnings: z.array(payrollCtcPreviewAmountLineSchema),
  /** What legacy computes on top of the template: employer PF/ESI (and any employer-cost template line). */
  employerContributions: z.string(),
  employerCosts: z.array(payrollCtcPreviewAmountLineSchema),
  /** `fixedGross + employerContributions`: what the company actually spends under this template. */
  costToCompany: z.string(),
  statutoryEmployee: z.string(),
  netBeforeIncomeTax: z.string(),
});

export const payrollCtcPreviewProposedSchema = z.object({
  basic: z.string(),
  minimumWage: z.string().nullable(),
  fixedGross: z.string(),
  earnings: z.array(payrollCtcPreviewAmountLineSchema.extend({ percentOfCtc: z.string() })),
  employerCosts: z.array(
    z.object({
      ruleCode: payrollRuleCodeSchema,
      amount: z.string(),
      /** The legacy component that would receive it, or null: no legacy component does. */
      legacyComponent: z.string().nullable(),
    }),
  ),
  employerCostsTotal: z.string(),
  /** `monthlyCtc - fixedGross - employerCostsTotal`: zero when the CTC is fully accounted for. */
  ctcUnaccounted: z.string(),
  statutoryEmployee: z.string(),
  netBeforeIncomeTax: z.string(),
});

export const payrollCtcPreviewDeltaSchema = z.object({
  basic: z.string().nullable(),
  fixedGross: z.string(),
  statutoryEmployee: z.string(),
  netBeforeIncomeTax: z.string(),
  /** Proposed cost to company (the CTC) less what the template costs today. */
  costToCompany: z.string(),
  annualNetBeforeIncomeTax: z.string(),
});

export const payrollCtcPreviewEmployeeSchema = z.object({
  employmentId: uuidSchema,
  monthlyCtc: z.string(),
  status: z.enum(['previewed', 'not_previewable']),
  /** Why the proposed side could not be computed: a code, never a message. */
  reasonCode: z.string().nullable(),
  missingFacts: z.array(z.string()),
  current: payrollCtcPreviewCurrentSchema.nullable(),
  proposed: payrollCtcPreviewProposedSchema.nullable(),
  delta: payrollCtcPreviewDeltaSchema.nullable(),
  /** Engine outputs no legacy component receives (employer bonus, gratuity, ...), from the same adapter the shadow uses. */
  unmappedOutputs: z.array(z.object({ ruleCode: payrollRuleCodeSchema, amount: z.string() })),
});

export const payrollCtcPreviewTemplateSchema = z.object({
  templateId: uuidSchema,
  code: z.string(),
  name: z.string(),
  gradeCode: z.string(),
  isActive: z.boolean(),
  version: z.number().int(),
  currency: z.string(),
  lines: z.array(
    z.object({
      componentCode: z.string(),
      componentName: z.string(),
      componentType: z.string(),
      calculationType: z.string(),
      value: z.string().nullable(),
      /** Whether the pack has a rule for this earning. `not_in_pack` stays `unsupported` in parity. */
      packStatus: z.enum(['in_pack', 'not_in_pack', 'not_applicable']),
    }),
  ),
  /** Template earnings the pack has no rule for (e.g. SPECIAL_ALLOWANCE). NOT a blocker: it is `unsupported`. */
  componentsNotInPack: z.array(z.string()),
  /** The employee count in scope, and how many are shown (a request may cap it). */
  employees: z.number().int().nonnegative(),
  shown: z.number().int().nonnegative(),
  truncated: z.boolean(),
  /** The same blockers readiness reports, derived from THESE figures. */
  blockers: z.array(payrollReadinessBlockerSchema),
  totals: z
    .object({
      previewed: z.number().int().nonnegative(),
      currentNetBeforeIncomeTax: z.string(),
      proposedNetBeforeIncomeTax: z.string(),
      deltaNetBeforeIncomeTax: z.string(),
    })
    .nullable(),
  items: z.array(payrollCtcPreviewEmployeeSchema),
});

export const payrollCtcPreviewSchema = z.object({
  legalEntityId: uuidSchema,
  asOf: dateOnlySchema,
  /** Always true: the preview mutates nothing. */
  readOnly: z.literal(true),
  periodDays: z.number().int(),
  rules: z.object({
    available: z.boolean(),
    reasonCode: z.string().nullable(),
    rulesChecksum: payrollSha256Schema.nullable(),
  }),
  templates: z.array(payrollCtcPreviewTemplateSchema),
  /** Employees in scope whose compensation has no CTC template: nothing to preview for them. */
  employeesWithoutTemplate: z.number().int().nonnegative(),
});
export type PayrollCtcPreview = z.infer<typeof payrollCtcPreviewSchema>;

export const payrollCtcPreviewQuerySchema = z.object({
  asOf: dateOnlySchema.optional(),
  templateId: uuidSchema.optional(),
  /** Employees shown per template. */
  limit: z.coerce.number().int().min(1).max(500).default(100),
});

// ---------------------------------------------------------------------------
// The router
// ---------------------------------------------------------------------------

export const payrollRolloutContract = c.router(
  {
    getRollout: {
      method: 'GET',
      path: '/entities/:legalEntityId',
      pathParams: payrollRolloutParamsSchema,
      responses: { 200: payrollRolloutSchema, ...authErrors, 404: errorBodySchema },
      summary: 'Which calculation path a legal entity runs',
      metadata: routeMeta('payroll.rules:read'),
    },

    setRolloutMode: {
      method: 'PUT',
      path: '/entities/:legalEntityId/mode',
      pathParams: payrollRolloutParamsSchema,
      body: setPayrollRolloutModeBodySchema,
      responses: {
        200: payrollRolloutSchema,
        ...authErrors,
        404: errorBodySchema,
        /** Entering `engine` is refused (`engine_entry_blocked`) while any readiness blocker remains. */
        422: errorBodySchema,
      },
      summary:
        'Switch an entity between legacy, shadow and engine mode (entering engine needs a clear readiness report)',
      // `manage` may switch shadow measurement on and off. A transition to `engine` ALSO needs
      // `payroll.rules:rollout`; `manage` alone can never enable it (ADR 0012, phase 4b).
      // AI-hidden: this changes which engine measures or pays payroll, not something a copilot proposes.
      metadata: routeMeta(['payroll.rules:manage', 'payroll.rules:rollout'], { ai: 'hidden' }),
    },

    getReadiness: {
      method: 'GET',
      path: '/entities/:legalEntityId/readiness',
      pathParams: payrollRolloutParamsSchema,
      query: payrollReadinessQuerySchema,
      responses: { 200: payrollReadinessSchema, ...authErrors, 404: errorBodySchema },
      summary: 'Is this entity ready for the engine, and what is in the way',
      // Employee attribute coverage is pay-shaped: payroll access as well as rules access.
      metadata: routeMeta(['payroll.rules:read', 'payroll:read']),
    },

    getCtcMigrationPreview: {
      method: 'GET',
      path: '/entities/:legalEntityId/ctc-migration-preview',
      pathParams: payrollRolloutParamsSchema,
      query: payrollCtcPreviewQuerySchema,
      responses: { 200: payrollCtcPreviewSchema, ...authErrors, 404: errorBodySchema },
      summary:
        'Per CTC template: what it pays today beside what the engine would pay as cost to company (read only)',
      // Per-employee pay figures: rules access as well as payroll access, as the readiness report requires.
      metadata: routeMeta(['payroll.rules:read', 'payroll:read']),
    },

    getPtEquivalence: {
      method: 'GET',
      path: '/entities/:legalEntityId/pt-equivalence',
      pathParams: payrollRolloutParamsSchema,
      query: payrollPtEquivalenceQuerySchema,
      responses: { 200: payrollPtEquivalenceSchema, ...authErrors, 404: errorBodySchema },
      summary:
        'Does the platform PT table reproduce the entity’s stored professional-tax slabs (read only)',
      // Tax-table configuration only: no employee or pay figure is in the response.
      metadata: routeMeta('payroll.rules:read'),
    },

    listEntityDefaults: {
      method: 'GET',
      path: '/defaults',
      query: listPayrollEntityFactDefaultsQuerySchema,
      responses: {
        200: z.object({ items: z.array(payrollEntityFactDefaultSchema) }),
        ...authErrors,
        404: errorBodySchema,
      },
      summary: 'Entity-level default facts',
      metadata: routeMeta('payroll.rules:read'),
    },

    createEntityDefault: {
      method: 'POST',
      path: '/defaults',
      headers: idempotencyHeadersSchema,
      body: createPayrollEntityFactDefaultBodySchema,
      responses: {
        201: payrollEntityFactDefaultSchema,
        ...authErrors,
        404: errorBodySchema,
        /** `overlapping_versions`: a default for that fact is already in force in that window. */
        409: errorBodySchema,
        /** The fact is unknown to the rules in force, or the wrong kind, or not entity-defaultable. */
        422: errorBodySchema,
      },
      summary: 'Define an explicit entity-level default for one fact',
      metadata: routeMeta('payroll.rules:manage', { ai: 'hidden' }),
    },

    endEntityDefault: {
      method: 'POST',
      path: '/defaults/:defaultId/end',
      pathParams: payrollEntityFactDefaultParamsSchema,
      body: endPayrollEntityFactDefaultBodySchema,
      responses: {
        200: payrollEntityFactDefaultSchema,
        ...authErrors,
        404: errorBodySchema,
        409: errorBodySchema,
      },
      summary: 'End an entity default on a date — defaults are never edited or deleted',
      metadata: routeMeta('payroll.rules:manage', { ai: 'hidden' }),
    },

    getRunParity: {
      method: 'GET',
      path: '/runs/:runId/parity',
      pathParams: payrollParityParamsSchema,
      query: payrollParityQuerySchema,
      responses: { 200: payrollParityReportSchema, ...authErrors, 404: errorBodySchema },
      summary: 'The shadow comparison of a run’s latest calculation, classified',
      // Comparison rows hold pay figures: payroll access as well as rules access.
      metadata: routeMeta(['payroll.rules:read', 'payroll:read'], { salaryPin: 'others' }),
    },
  },
  { pathPrefix: '/payroll/rollout' },
);
